import { useState, useCallback } from 'react';
import { GameState, Player, Role, GamePhase, VoiceType, NightEvent, NarrativeTone, GameRules, DEFAULT_GAME_RULES } from '@/types/game';

const generateId = () => Math.random().toString(36).substring(2, 9);

const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const assignRoles = (playerCount: number, rules: GameRules): Role[] => {
  const roles: Role[] = [];
  const { enabledRoles, werewolfCount } = rules;
  
  // Add special village roles if enabled
  if (enabledRoles.seer) {
    roles.push('seer');
  }
  
  if (enabledRoles.witch) {
    roles.push('witch');
  }
  
  if (enabledRoles.hunter) {
    roles.push('hunter');
  }
  
  if (enabledRoles.cupid) {
    roles.push('cupid');
  }
  
  // Add white wolf if enabled (counts as one of the werewolves)
  if (enabledRoles.white_wolf) {
    roles.push('white_wolf');
  }
  
  // Add werewolves (minus 1 if white wolf is present since it counts as a werewolf)
  const regularWerewolves = enabledRoles.white_wolf 
    ? Math.max(0, werewolfCount - 1) 
    : werewolfCount;
  
  for (let i = 0; i < regularWerewolves; i++) {
    roles.push('werewolf');
  }
  
  // Rest are villagers
  while (roles.length < playerCount) {
    roles.push('villager');
  }
  
  return shuffleArray(roles);
};

export const useGameState = () => {
  const [gameState, setGameState] = useState<GameState | null>(null);

  const createGame = useCallback((playerNames: string[], voiceType: VoiceType, narrativeTone: NarrativeTone = 'taquin', rules: GameRules = DEFAULT_GAME_RULES) => {
    const roles = assignRoles(playerNames.length, rules);
    const players: Player[] = playerNames.map((name, index) => ({
      id: generateId(),
      name,
      role: roles[index],
      isAlive: true,
      isLover: false,
    }));

    const newGame: GameState = {
      id: generateId(),
      phase: 'lobby',
      players,
      currentTurn: 1,
      voiceType,
      narrativeTone,
      witchPotions: {
        hasHealPotion: true,
        hasKillPotion: true,
      },
      loversChosen: false,
      nightEvents: [],
      seerRevealedPlayers: [],
      mayorId: undefined,
      pendingMayorElection: true, // Mayor election happens at start of first day
      tiebreakerCandidates: undefined,
    };

    setGameState(newGame);
    return newGame;
  }, []);

  const setPhase = useCallback((phase: GamePhase) => {
    setGameState(prev => prev ? { ...prev, phase } : null);
  }, []);

  const startNight = useCallback(() => {
    setGameState(prev => prev ? { 
      ...prev, 
      phase: 'night',
      nightAction: {},
      eliminatedTonight: undefined,
      hunterRevengeTarget: undefined,
    } : null);
  }, []);

  const werewolfVote = useCallback((targetId: string) => {
    setGameState(prev => {
      if (!prev) return null;
      const target = prev.players.find(p => p.id === targetId);
      const newEvent: NightEvent = {
        turn: prev.currentTurn,
        type: 'werewolf_kill',
        targetId,
        targetRole: target?.role,
      };
      return {
        ...prev,
        nightAction: { ...prev.nightAction, werewolfTarget: targetId },
        nightEvents: [...prev.nightEvents, newEvent],
      };
    });
  }, []);

  const seerReveal = useCallback((targetId: string) => {
    setGameState(prev => {
      if (!prev) return null;
      const target = prev.players.find(p => p.id === targetId);
      const seer = prev.players.find(p => p.role === 'seer');
      const newEvent: NightEvent = {
        turn: prev.currentTurn,
        type: 'seer_reveal',
        actorId: seer?.id,
        targetId,
        targetRole: target?.role,
      };
      return {
        ...prev,
        nightAction: { ...prev.nightAction, seerTarget: targetId },
        nightEvents: [...prev.nightEvents, newEvent],
        seerRevealedPlayers: [...prev.seerRevealedPlayers, targetId],
      };
    });
  }, []);

  const witchHeal = useCallback(() => {
    setGameState(prev => {
      if (!prev) return null;
      const witch = prev.players.find(p => p.role === 'witch');
      const savedPlayer = prev.players.find(p => p.id === prev.nightAction?.werewolfTarget);
      const newEvent: NightEvent = {
        turn: prev.currentTurn,
        type: 'witch_save',
        actorId: witch?.id,
        targetId: prev.nightAction?.werewolfTarget,
        targetRole: savedPlayer?.role,
        saved: true,
      };
      return {
        ...prev,
        nightAction: { ...prev.nightAction, witchHealUsed: true },
        witchPotions: { ...prev.witchPotions, hasHealPotion: false },
        nightEvents: [...prev.nightEvents, newEvent],
      };
    });
  }, []);

  const witchKill = useCallback((targetId: string) => {
    setGameState(prev => {
      if (!prev) return null;
      const witch = prev.players.find(p => p.role === 'witch');
      const target = prev.players.find(p => p.id === targetId);
      const newEvent: NightEvent = {
        turn: prev.currentTurn,
        type: 'witch_kill',
        actorId: witch?.id,
        targetId,
        targetRole: target?.role,
      };
      return {
        ...prev,
        nightAction: { ...prev.nightAction, witchKillTarget: targetId },
        witchPotions: { ...prev.witchPotions, hasKillPotion: false },
        nightEvents: [...prev.nightEvents, newEvent],
      };
    });
  }, []);

  const whiteWolfKill = useCallback((targetId: string) => {
    setGameState(prev => {
      if (!prev) return null;
      const whiteWolf = prev.players.find(p => p.role === 'white_wolf');
      const target = prev.players.find(p => p.id === targetId);
      const newEvent: NightEvent = {
        turn: prev.currentTurn,
        type: 'white_wolf_kill',
        actorId: whiteWolf?.id,
        targetId,
        targetRole: target?.role,
      };
      return {
        ...prev,
        nightAction: { ...prev.nightAction, whiteWolfTarget: targetId },
        nightEvents: [...prev.nightEvents, newEvent],
      };
    });
  }, []);

  const cupidChooseLovers = useCallback((lover1Id: string, lover2Id: string) => {
    setGameState(prev => {
      if (!prev) return null;
      
      const updatedPlayers = prev.players.map(p => {
        if (p.id === lover1Id) {
          return { ...p, isLover: true, loverId: lover2Id };
        }
        if (p.id === lover2Id) {
          return { ...p, isLover: true, loverId: lover1Id };
        }
        return p;
      });

      const cupid = prev.players.find(p => p.role === 'cupid');
      const newEvent: NightEvent = {
        turn: prev.currentTurn,
        type: 'cupid_lovers',
        actorId: cupid?.id,
        targetId: lover1Id,
        secondTargetId: lover2Id,
      };

      return {
        ...prev,
        players: updatedPlayers,
        nightAction: { ...prev.nightAction, cupidLovers: [lover1Id, lover2Id] },
        loversChosen: true,
        nightEvents: [...prev.nightEvents, newEvent],
      };
    });
  }, []);

  const hunterRevenge = useCallback((targetId: string) => {
    setGameState(prev => {
      if (!prev) return null;
      
      const hunter = prev.players.find(p => p.role === 'hunter');
      const target = prev.players.find(p => p.id === targetId);
      
      let updatedPlayers = prev.players.map(p => 
        p.id === targetId ? { ...p, isAlive: false } : p
      );

      const newEvents: NightEvent[] = [{
        turn: prev.currentTurn,
        type: 'hunter_revenge',
        actorId: hunter?.id,
        targetId,
        targetRole: target?.role,
      }];

      // Check if hunter killed a lover
      const killedPlayer = updatedPlayers.find(p => p.id === targetId);
      if (killedPlayer?.isLover && killedPlayer.loverId) {
        const lover = prev.players.find(p => p.id === killedPlayer.loverId);
        updatedPlayers = updatedPlayers.map(p =>
          p.id === killedPlayer.loverId ? { ...p, isAlive: false } : p
        );
        newEvents.push({
          turn: prev.currentTurn,
          type: 'lover_death',
          targetId: killedPlayer.loverId,
          targetRole: lover?.role,
        });
      }

      // Check win conditions
      const alivePlayers = updatedPlayers.filter(p => p.isAlive);
      const aliveWerewolves = alivePlayers.filter(p => p.role === 'werewolf');
      const aliveWhiteWolf = alivePlayers.find(p => p.role === 'white_wolf');
      const aliveWolfTeam = alivePlayers.filter(p => p.role === 'werewolf' || p.role === 'white_wolf');
      const aliveVillagers = alivePlayers.filter(p => p.role !== 'werewolf' && p.role !== 'white_wolf');
      const aliveLovers = alivePlayers.filter(p => p.isLover);

      let winner: 'werewolves' | 'villagers' | 'lovers' | 'white_wolf' | undefined;
      
      // Check white wolf solo victory
      if (aliveWhiteWolf && alivePlayers.length === 1) {
        winner = 'white_wolf';
      }
      
      // Check if lovers won
      if (!winner && aliveLovers.length === 2 && alivePlayers.length === 2) {
        const loversRoles = aliveLovers.map(l => l.role);
        if ((loversRoles.includes('werewolf') || loversRoles.includes('white_wolf')) && 
            loversRoles.some(r => r !== 'werewolf' && r !== 'white_wolf')) {
          winner = 'lovers';
        }
      }
      
      if (!winner) {
        if (aliveWolfTeam.length === 0) {
          winner = 'villagers';
        } else if (aliveWolfTeam.length >= aliveVillagers.length) {
          if (aliveWerewolves.length === 0 && aliveWhiteWolf) {
            winner = 'white_wolf';
          } else {
            winner = 'werewolves';
          }
        }
      }

      return {
        ...prev,
        phase: winner ? 'end' : 'night',
        players: updatedPlayers,
        hunterRevengeTarget: targetId,
        winner,
        nightEvents: [...prev.nightEvents, ...newEvents],
      };
    });
  }, []);

  const killPlayer = useCallback((playerId: string): { hunterDied: boolean; loversAffected: string[] } => {
    let hunterDied = false;
    let loversAffected: string[] = [];

    setGameState(prev => {
      if (!prev) return null;

      let updatedPlayers = prev.players.map(p => 
        p.id === playerId ? { ...p, isAlive: false } : p
      );

      const killedPlayer = updatedPlayers.find(p => p.id === playerId);
      
      // Check if hunter died
      if (killedPlayer?.role === 'hunter') {
        hunterDied = true;
      }

      // Check if a lover died
      if (killedPlayer?.isLover && killedPlayer.loverId) {
        loversAffected = [playerId, killedPlayer.loverId];
        updatedPlayers = updatedPlayers.map(p =>
          p.id === killedPlayer.loverId ? { ...p, isAlive: false } : p
        );
        
        // Check if the other lover is the hunter
        const otherLover = updatedPlayers.find(p => p.id === killedPlayer.loverId);
        if (otherLover?.role === 'hunter') {
          hunterDied = true;
        }
      }

      return { ...prev, players: updatedPlayers };
    });

    return { hunterDied, loversAffected };
  }, []);

  const startDay = useCallback(() => {
    setGameState(prev => {
      if (!prev) return null;
      
      // Determine who dies tonight
      let eliminatedId = prev.nightAction?.werewolfTarget;
      
      // Witch can save the werewolf target
      if (prev.nightAction?.witchHealUsed && eliminatedId) {
        eliminatedId = undefined;
      }

      // Process deaths
      let updatedPlayers = prev.players;
      const deathIds: string[] = [];
      
      // Werewolf victim (if not saved by witch)
      if (eliminatedId) {
        deathIds.push(eliminatedId);
      }
      
      // Witch kill target
      if (prev.nightAction?.witchKillTarget) {
        deathIds.push(prev.nightAction.witchKillTarget);
      }
      
      // White Wolf kill target (kills another werewolf)
      if (prev.nightAction?.whiteWolfTarget) {
        deathIds.push(prev.nightAction.whiteWolfTarget);
      }

      // Apply deaths and check for lovers
      let hunterDied = false;
      const processedDeaths = new Set<string>();
      let mayorDied = false;
      
      deathIds.forEach(id => {
        if (processedDeaths.has(id)) return;
        processedDeaths.add(id);
        
        const player = prev.players.find(p => p.id === id);
        if (player?.isAlive) {
          updatedPlayers = updatedPlayers.map(p => 
            p.id === id ? { ...p, isAlive: false } : p
          );
          
          if (player.role === 'hunter') {
            hunterDied = true;
          }
          
          // Check if mayor died
          if (id === prev.mayorId) {
            mayorDied = true;
          }
          
          // If lover dies, other lover dies too
          if (player.isLover && player.loverId) {
            const lover = prev.players.find(p => p.id === player.loverId);
            if (lover?.isAlive && !processedDeaths.has(lover.id)) {
              processedDeaths.add(lover.id);
              updatedPlayers = updatedPlayers.map(p => 
                p.id === lover.id ? { ...p, isAlive: false } : p
              );
              if (lover.role === 'hunter') {
                hunterDied = true;
              }
              // Check if lover was the mayor
              if (lover.id === prev.mayorId) {
                mayorDied = true;
              }
            }
          }
        }
      });

      // Check win conditions after night kill
      const alivePlayers = updatedPlayers.filter(p => p.isAlive);
      const aliveWerewolves = alivePlayers.filter(p => p.role === 'werewolf');
      const aliveWhiteWolf = alivePlayers.find(p => p.role === 'white_wolf');
      const aliveWolfTeam = alivePlayers.filter(p => p.role === 'werewolf' || p.role === 'white_wolf');
      const aliveVillagers = alivePlayers.filter(p => p.role !== 'werewolf' && p.role !== 'white_wolf');
      const aliveLovers = alivePlayers.filter(p => p.isLover);

      let winner: 'werewolves' | 'villagers' | 'lovers' | 'white_wolf' | undefined;
      
      // Check white wolf solo victory (only white wolf survives)
      if (aliveWhiteWolf && alivePlayers.length === 1) {
        winner = 'white_wolf';
      }
      
      // Check lovers victory
      if (!winner && aliveLovers.length === 2 && alivePlayers.length === 2) {
        const loversRoles = aliveLovers.map(l => l.role);
        if ((loversRoles.includes('werewolf') || loversRoles.includes('white_wolf')) && 
            loversRoles.some(r => r !== 'werewolf' && r !== 'white_wolf')) {
          winner = 'lovers';
        }
      }
      
      if (!winner) {
        if (aliveWolfTeam.length === 0) {
          winner = 'villagers';
        } else if (aliveWolfTeam.length >= aliveVillagers.length) {
          // If only white wolf remains in wolf team, he wins solo
          if (aliveWerewolves.length === 0 && aliveWhiteWolf) {
            winner = 'white_wolf';
          } else {
            winner = 'werewolves';
          }
        }
      }

      // If hunter died and no winner yet, go to hunter revenge phase
      const nextPhase = hunterDied && !winner ? 'hunter-revenge' : (winner ? 'end' : 'day');

      return {
        ...prev,
        phase: nextPhase,
        players: updatedPlayers,
        eliminatedTonight: Array.from(processedDeaths),
        winner,
        mayorId: mayorDied ? undefined : prev.mayorId,
        pendingMayorElection: mayorDied || prev.pendingMayorElection,
      };
    });
  }, []);

  const castVote = useCallback((voterId: string, targetId: string) => {
    setGameState(prev => {
      if (!prev) return null;
      return {
        ...prev,
        players: prev.players.map(p => 
          p.id === voterId ? { ...p, votedFor: targetId } : p
        ),
      };
    });
  }, []);

  const executeVote = useCallback(() => {
    setGameState(prev => {
      if (!prev) return null;

      // Count votes
      const voteCount: Record<string, number> = {};
      prev.players.filter(p => p.isAlive && p.votedFor).forEach(p => {
        if (p.votedFor) {
          voteCount[p.votedFor] = (voteCount[p.votedFor] || 0) + 1;
        }
      });

      // Find player(s) with most votes
      let maxVotes = 0;
      const topVoted: string[] = [];
      Object.entries(voteCount).forEach(([id, count]) => {
        if (count > maxVotes) {
          maxVotes = count;
          topVoted.length = 0;
          topVoted.push(id);
        } else if (count === maxVotes) {
          topVoted.push(id);
        }
      });

      // Check for tie
      if (topVoted.length > 1) {
        // If there's a mayor, go to tiebreaker phase
        if (prev.mayorId && prev.players.find(p => p.id === prev.mayorId)?.isAlive) {
          return {
            ...prev,
            phase: 'mayor-tiebreaker' as const,
            tiebreakerCandidates: topVoted,
            players: prev.players.map(p => ({ ...p, votedFor: undefined })),
          };
        }
        // No mayor alive - random selection among tied players
        const randomIndex = Math.floor(Math.random() * topVoted.length);
        topVoted.splice(0, topVoted.length, topVoted[randomIndex]);
      }

      const eliminatedId = topVoted[0];

      let updatedPlayers = prev.players.map(p => ({
        ...p,
        isAlive: p.id === eliminatedId ? false : p.isAlive,
        votedFor: undefined,
      }));

      let hunterDied = false;
      const eliminatedPlayer = prev.players.find(p => p.id === eliminatedId);
      
      if (eliminatedPlayer?.role === 'hunter') {
        hunterDied = true;
      }

      // Check if mayor was eliminated - trigger new election
      let newMayorId = prev.mayorId;
      let pendingMayorElection = false;
      if (eliminatedId === prev.mayorId) {
        newMayorId = undefined;
        pendingMayorElection = true;
      }

      // If eliminated player is a lover, kill their partner too
      if (eliminatedPlayer?.isLover && eliminatedPlayer.loverId) {
        const lover = updatedPlayers.find(p => p.id === eliminatedPlayer.loverId);
        if (lover?.isAlive) {
          updatedPlayers = updatedPlayers.map(p =>
            p.id === eliminatedPlayer.loverId ? { ...p, isAlive: false } : p
          );
          if (lover.role === 'hunter') {
            hunterDied = true;
          }
          // Check if lover was the mayor
          if (eliminatedPlayer.loverId === prev.mayorId) {
            newMayorId = undefined;
            pendingMayorElection = true;
          }
        }
      }

      // Check win conditions
      const alivePlayers = updatedPlayers.filter(p => p.isAlive);
      const aliveWerewolves = alivePlayers.filter(p => p.role === 'werewolf');
      const aliveWhiteWolf = alivePlayers.find(p => p.role === 'white_wolf');
      const aliveWolfTeam = alivePlayers.filter(p => p.role === 'werewolf' || p.role === 'white_wolf');
      const aliveVillagers = alivePlayers.filter(p => p.role !== 'werewolf' && p.role !== 'white_wolf');
      const aliveLovers = alivePlayers.filter(p => p.isLover);

      let winner: 'werewolves' | 'villagers' | 'lovers' | 'white_wolf' | undefined;
      
      // Check white wolf solo victory
      if (aliveWhiteWolf && alivePlayers.length === 1) {
        winner = 'white_wolf';
      }
      
      if (!winner && aliveLovers.length === 2 && alivePlayers.length === 2) {
        const loversRoles = aliveLovers.map(l => l.role);
        if ((loversRoles.includes('werewolf') || loversRoles.includes('white_wolf')) && 
            loversRoles.some(r => r !== 'werewolf' && r !== 'white_wolf')) {
          winner = 'lovers';
        }
      }
      
      if (!winner) {
        if (aliveWolfTeam.length === 0) {
          winner = 'villagers';
        } else if (aliveWolfTeam.length >= aliveVillagers.length) {
          if (aliveWerewolves.length === 0 && aliveWhiteWolf) {
            winner = 'white_wolf';
          } else {
            winner = 'werewolves';
          }
        }
      }

      // If hunter died and no winner, go to hunter revenge
      const nextPhase = hunterDied && !winner ? 'hunter-revenge' : 'elimination';

      return {
        ...prev,
        phase: nextPhase,
        players: updatedPlayers,
        eliminatedTonight: eliminatedId ? [eliminatedId] : [],
        winner,
        currentTurn: prev.currentTurn + 1,
        mayorId: newMayorId,
        pendingMayorElection,
        tiebreakerCandidates: undefined,
      };
    });
  }, []);

  const electMayor = useCallback((mayorId: string) => {
    setGameState(prev => {
      if (!prev) return null;
      return {
        ...prev,
        mayorId,
        pendingMayorElection: false,
        phase: 'voting' as const,
      };
    });
  }, []);

  const mayorTiebreaker = useCallback((eliminatedId: string) => {
    setGameState(prev => {
      if (!prev) return null;

      let updatedPlayers = prev.players.map(p => ({
        ...p,
        isAlive: p.id === eliminatedId ? false : p.isAlive,
        votedFor: undefined,
      }));

      let hunterDied = false;
      const eliminatedPlayer = prev.players.find(p => p.id === eliminatedId);
      
      if (eliminatedPlayer?.role === 'hunter') {
        hunterDied = true;
      }

      // Check if mayor was eliminated (shouldn't happen in tiebreaker, but safety check)
      let newMayorId = prev.mayorId;
      let pendingMayorElection = false;

      // If eliminated player is a lover, kill their partner too
      if (eliminatedPlayer?.isLover && eliminatedPlayer.loverId) {
        const lover = updatedPlayers.find(p => p.id === eliminatedPlayer.loverId);
        if (lover?.isAlive) {
          updatedPlayers = updatedPlayers.map(p =>
            p.id === eliminatedPlayer.loverId ? { ...p, isAlive: false } : p
          );
          if (lover.role === 'hunter') {
            hunterDied = true;
          }
          // Check if lover was the mayor
          if (eliminatedPlayer.loverId === prev.mayorId) {
            newMayorId = undefined;
            pendingMayorElection = true;
          }
        }
      }

      // Check win conditions
      const alivePlayers = updatedPlayers.filter(p => p.isAlive);
      const aliveWerewolves = alivePlayers.filter(p => p.role === 'werewolf');
      const aliveWhiteWolf = alivePlayers.find(p => p.role === 'white_wolf');
      const aliveWolfTeam = alivePlayers.filter(p => p.role === 'werewolf' || p.role === 'white_wolf');
      const aliveVillagers = alivePlayers.filter(p => p.role !== 'werewolf' && p.role !== 'white_wolf');
      const aliveLovers = alivePlayers.filter(p => p.isLover);

      let winner: 'werewolves' | 'villagers' | 'lovers' | 'white_wolf' | undefined;
      
      if (aliveWhiteWolf && alivePlayers.length === 1) {
        winner = 'white_wolf';
      }
      
      if (!winner && aliveLovers.length === 2 && alivePlayers.length === 2) {
        const loversRoles = aliveLovers.map(l => l.role);
        if ((loversRoles.includes('werewolf') || loversRoles.includes('white_wolf')) && 
            loversRoles.some(r => r !== 'werewolf' && r !== 'white_wolf')) {
          winner = 'lovers';
        }
      }
      
      if (!winner) {
        if (aliveWolfTeam.length === 0) {
          winner = 'villagers';
        } else if (aliveWolfTeam.length >= aliveVillagers.length) {
          if (aliveWerewolves.length === 0 && aliveWhiteWolf) {
            winner = 'white_wolf';
          } else {
            winner = 'werewolves';
          }
        }
      }

      const nextPhase = hunterDied && !winner ? 'hunter-revenge' : 'elimination';

      return {
        ...prev,
        phase: nextPhase,
        players: updatedPlayers,
        eliminatedTonight: eliminatedId ? [eliminatedId] : [],
        winner,
        currentTurn: prev.currentTurn + 1,
        mayorId: newMayorId,
        pendingMayorElection,
        tiebreakerCandidates: undefined,
      };
    });
  }, []);

  const getAlivePlayers = useCallback(() => {
    return gameState?.players.filter(p => p.isAlive) || [];
  }, [gameState]);

  const getPlayer = useCallback((id: string) => {
    return gameState?.players.find(p => p.id === id);
  }, [gameState]);

  const resetGame = useCallback(() => {
    setGameState(null);
  }, []);

  return {
    gameState,
    createGame,
    setPhase,
    startNight,
    startDay,
    werewolfVote,
    seerReveal,
    witchHeal,
    witchKill,
    whiteWolfKill,
    cupidChooseLovers,
    hunterRevenge,
    killPlayer,
    castVote,
    executeVote,
    electMayor,
    mayorTiebreaker,
    getAlivePlayers,
    getPlayer,
    resetGame,
  };
};
